import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCivilLitigationWizardContext } from '../../../hooks/useCaseRequestWizard';
import { CONTACT_NUMBER_LENGTH, contactNumberError, exceedsMax } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { CivilLitigationWizardActions } from './CivilLitigationWizardActions';
import { civilLitigationWizardPath, CivilLitigationWizardShell, FieldError, inpClass } from './CivilLitigationWizardShell';
import { validateCivilLitigationStep } from './civilLitigationStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';
import { isCivilLitigationOriginalCourtsCategory } from './civilLitigationHelpers';

const MAX_NAME = 255;
const MAX_DESC = 2000;
const MAX_CONTACT = CONTACT_NUMBER_LENGTH;

export default function CivilLitigationStep2PartyDetailsPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useCivilLitigationWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);
  const isOriginalCourts = isCivilLitigationOriginalCourtsCategory(
    draft.fileCategory,
    draft.subFileCategoryName,
  );

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateCivilLitigationStep(2, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const updateCorpName = (index: number, raw: string) => {
    const value = maxLen(`publicCorp-${index}`, raw, MAX_NAME);
    setDraft((d) => ({
      ...d,
      publicCorporationNames: d.publicCorporationNames.map((n, i) => (i === index ? value : n)),
    }));
  };

  const addCorp = () => setDraft((d) => ({ ...d, publicCorporationNames: [...d.publicCorporationNames, ''] }));
  const removeCorp = (index: number) =>
    setDraft((d) => ({
      ...d,
      publicCorporationNames:
        d.publicCorporationNames.length > 1
          ? d.publicCorporationNames.filter((_, i) => i !== index)
          : d.publicCorporationNames,
    }));

  return (
    <CivilLitigationWizardShell currentStep={2} draft={draft}>
      <SectionCard title={f.sectionPartyDetails}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.plaintiffApplicantName} <span className="req">*</span>
            </label>
            <input
              id="clit-plaintiff"
              className={inpClass(Boolean(errors.plaintiffName))}
              type="text"
              value={draft.plaintiffName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, plaintiffName: maxLen('plaintiffName', e.target.value, MAX_NAME) }));
              }}
              placeholder={f.enterPlaintiffApplicantName}
            />
            <FieldError message={errors.plaintiffName} />
          </div>
          <div className="ccr-fg">
            <label>
              {f.defendantRespondentName} <span className="req">*</span>
            </label>
            <input
              id="clit-defendant"
              className={inpClass(Boolean(errors.defendantName))}
              type="text"
              value={draft.defendantName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, defendantName: maxLen('defendantName', e.target.value, MAX_NAME) }));
              }}
              placeholder={f.enterDefendantRespondentName}
            />
            <FieldError message={errors.defendantName} />
          </div>
        </div>

        <div className="ccr-fg" id="clit-partyBodyType" style={{ marginBottom: 14 }}>
          <label>
            {f.selectOneOfFollowing}: <span className="req">*</span>
          </label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="clit-partyBodyType"
                checked={draft.involvesGovernmentBody === 'yes'}
                onChange={() => {
                  setDraft((d) => ({
                    ...d,
                    involvesGovernmentBody: 'yes',
                    involvesPublicCorporation: 'no',
                    publicCorporationNames: [''],
                  }));
                  clearErr('partyBodyType');
                  clearErr('publicCorporationNames');
                }}
              />
              <span>{f.governmentMinistryDepartmentAlt}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="clit-partyBodyType"
                checked={draft.involvesPublicCorporation === 'yes'}
                onChange={() => {
                  setDraft((d) => ({
                    ...d,
                    involvesGovernmentBody: 'no',
                    involvesPublicCorporation: 'yes',
                    publicCorporationNames: d.publicCorporationNames.length ? d.publicCorporationNames : [''],
                  }));
                  clearErr('partyBodyType');
                }}
              />
              <span>{f.publicCorporationStatutoryAlt}</span>
            </label>
          </div>
          <FieldError message={errors.partyBodyType} />
        </div>

        {!isOriginalCourts && draft.involvesPublicCorporation === 'yes' ? (
          <div style={{ marginBottom: 14 }}>
            {draft.publicCorporationNames.map((name, index) => (
              <div key={index} className="ccr-fg" style={{ marginBottom: 10 }}>
                <label>
                  {f.publicCorporationNames} {draft.publicCorporationNames.length > 1 ? `#${index + 1}` : null}
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <input
                    className={inpClass(Boolean(errors[`publicCorp-${index}`] || errors.publicCorporationNames))}
                    type="text"
                    value={name}
                    onChange={(e) => updateCorpName(index, e.target.value)}
                    placeholder={f.enterInstitutionName}
                  />
                  {draft.publicCorporationNames.length > 1 ? (
                    <button className="ccr-btn-ol" type="button" onClick={() => removeCorp(index)}>
                      {f.remove}
                    </button>
                  ) : null}
                </div>
                <FieldError message={errors[`publicCorp-${index}`] ?? errors.publicCorporationNames} />
              </div>
            ))}
            <button className="ccr-btn-ol" type="button" onClick={addCorp}>
              {f.addCorporation}
            </button>
          </div>
        ) : null}

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.otherInvolvedInstitutions}</label>
          <textarea
            className={inpClass(Boolean(errors.otherInvolvedInstitutions))}
            rows={3}
            value={draft.otherInvolvedInstitutions}
            onChange={(e) =>
              setDraft((d) => ({ ...d, otherInvolvedInstitutions: maxLen('otherInvolvedInstitutions', e.target.value, MAX_DESC) }))
            }
            placeholder={f.otherRelatedInstitutionsHint}
          />
          <FieldError message={errors.otherInvolvedInstitutions} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
          <div className="ccr-fg">
            <label>
              {f.legalOfficerNameAlt} <span className="req">*</span>
            </label>
            <input
              id="clit-legalOfficer"
              className={inpClass(Boolean(errors.legalOfficerName))}
              type="text"
              value={draft.legalOfficerName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, legalOfficerName: maxLen('legalOfficerName', e.target.value, MAX_NAME) }));
              }}
              placeholder={f.enterLegalOfficerNameInCharge}
            />
            <FieldError message={errors.legalOfficerName} />
          </div>
          <div className="ccr-fg">
            <label>
              {f.contactNumber} <span className="req">*</span>
            </label>
            <input
              id="clit-legalContact"
              className={inpClass(Boolean(errors.legalOfficerContact))}
              type="text"
              inputMode="numeric"
              value={draft.legalOfficerContact}
              onChange={(e) => {
                setDraft((d) => ({
                  ...d,
                  legalOfficerContact: e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT),
                }));
                clearErr('legalOfficerContact');
              }}
              onBlur={() => {
                const err = contactNumberError(draft.legalOfficerContact);
                if (err) setErrors((e) => ({ ...e, legalOfficerContact: err }));
              }}
              placeholder={f.contactNumberExample}
            />
            <FieldError message={errors.legalOfficerContact} />
          </div>
        </div>
      </SectionCard>

      <CivilLitigationWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={2}
        onBack={() => nav(civilLitigationWizardPath(1, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(civilLitigationWizardPath(3, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </CivilLitigationWizardShell>
  );
}
